import { Collapse } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import { _getSingleEntityOptions } from '@/api/entity/queries';
import { entityRole, GetEntityTeamRes } from '@/api/entity/types';
import { _getEntityViewsTeamAssociatesInfinite } from '@/api/entity-views/infinite-queries';
import { useGetUserProfileSuspense } from '@/api/user-dashboard/queries';
import ListFilterIcon from '@/assets/svg/ui-elements/list-filter.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import SearchIcon from '@/assets/svg/ui-elements/search.svg';
import useDebounce from '@/global-hooks/use-debounce';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { PagePaths } from '@/routes/page-paths';
import {
  Button,
  EmptyState,
  getTableMinWidth,
  Input,
  REVEAL_DURATION_MS,
  SelectTriggerButton,
  Spinner,
  Table,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import { useInfiniteScroll } from '@/shared/hooks/use-infinite-scroll';
import { DeniedReasonTrigger, Permission, useCan } from '@/shared/modules/permissions';
import { useEntitySlice, useSelectedUserConfigSlice } from '@/stores/selectors';

import { buildMemberColumns, MemberRow } from './member-columns';
import { MemberFundsTable } from './member-funds-table';
import {
  ExpandedRowWrapper,
  HeaderRow,
  LoadMoreSentinel,
  LoadMoreSpinner,
  SearchField,
  ToolbarRow,
  UserDetailsRoot,
} from './user-details.styled';
import {
  countActiveFilters,
  IUserDetailsFilters,
  UserDetailsFiltersDrawer,
} from './user-details-filters-drawer';

export const NO_MEMBERS_MESSAGE = 'No team members yet';
export const NO_MATCHING_MEMBERS_MESSAGE = 'No team members match those filters';
export const ENTITY_NOT_SUBMITTED_TOOLTIP = 'Entity is not submitted';
export const INVITE_BLOCKED_TOOLTIP = 'You do not have permission to invite a team member.';

const SEARCH_DEBOUNCE_MS = 300;

interface UserDetailsProps {
  entityId?: string;
}

export const UserDetails = ({ entityId: entityIdProp }: UserDetailsProps = {}) => {
  const { selectedEntityId } = useSelectedUserConfigSlice();
  const entityId = entityIdProp ?? selectedEntityId;
  const navigate = useNavigate();

  const [filters, setFilters] = useState<IUserDetailsFilters>({ role: '', status: '', search: '' });
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const [openIds, setOpenIds] = useState<string[]>([]);

  const debouncedSearch = useDebounce(filters.search, SEARCH_DEBOUNCE_MS);
  const { stepForms } = useEntitySlice();
  const { user } = useGetUserProfileSuspense();
  // `CreateEntityMemberInvitationHandler` gates on `EntityManageTeam`.
  const canInviteMember = useCan(Permission.EntityManageTeam);

  const { items, isLoading, isFetchingNextPage, loadMoreRef, data } = useInfiniteScroll(
    _getEntityViewsTeamAssociatesInfinite({
      id: entityId,
      search: debouncedSearch || undefined,
      role: filters.role || undefined,
      status: filters.status || undefined,
    }),
    (page) => page.items,
  );

  const { data: currentEntity } = useQuery(_getSingleEntityOptions(entityIdProp ?? stepForms.id));

  const total = data?.pages[0]?.total ?? 0;

  const lastAdminId = useMemo(() => {
    const admins = items.filter((item) => item.role === entityRole.Admin);

    return admins.length === 1 ? admins[0].entityMemberId : null;
  }, [items]);

  const entityTeamForModal = useMemo<GetEntityTeamRes>(
    () => ({ items, total, page: 1, limit: 20 }),
    [items, total],
  );

  const { handleOpen } = useGlobalModal('TEAM_MEMBER_ADD_MODAL', {
    entityTeam: entityTeamForModal,
    entityId: entityIdProp,
  });

  const isEntityApprovalStatusDraft = currentEntity?.entityApprovalStatus === 'Draft';
  const isAddMemberDisabled = isEntityApprovalStatusDraft || !canInviteMember;
  const addMemberBlockedReason = !canInviteMember
    ? INVITE_BLOCKED_TOOLTIP
    : isEntityApprovalStatusDraft
      ? ENTITY_NOT_SUBMITTED_TOOLTIP
      : '';

  const columns = useMemo(
    () => buildMemberColumns({ currentUserId: user.id, entityId: entityIdProp }),
    [user.id, entityIdProp],
  );

  const rows = useMemo<MemberRow[]>(
    () =>
      items.map((member, index) => {
        const key = `${member.userId}_${index}`;

        return {
          key,
          member,
          isOpen: openIds.includes(key),
          isLastAdmin: !!member.entityMemberId && member.entityMemberId === lastAdminId,
          onToggle: () =>
            setOpenIds((prev) =>
              prev.includes(key) ? prev.filter((id) => id !== key) : [...prev, key],
            ),
          onClick:
            member.userId && !openIds.includes(key)
              ? () => navigate(generatePath(PagePaths.userProfile, { id: member.userId as string }))
              : undefined,
        };
      }),
    [items, openIds, lastAdminId, navigate],
  );

  const activeFiltersCount = countActiveFilters(filters);

  return (
    <UserDetailsRoot>
      <HeaderRow>
        <Typography variant="h5" fontWeight={600}>
          All Team Members
        </Typography>

        <Tooltip title={addMemberBlockedReason} side="top">
          <DeniedReasonTrigger denied={isAddMemberDisabled}>
            <Button color="primary" onClick={handleOpen} disabled={isAddMemberDisabled}>
              <PlusIcon />
              Add new member
            </Button>
          </DeniedReasonTrigger>
        </Tooltip>
      </HeaderRow>

      <ToolbarRow>
        <SearchField>
          <Input
            size="lg"
            value={filters.search}
            placeholder="Search"
            startIcon={<SearchIcon />}
            aria-label="Search team members"
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
          />
        </SearchField>

        <SelectTriggerButton
          label="Show Filters"
          size="lg"
          startIcon={<ListFilterIcon />}
          count={activeFiltersCount}
          open={isFiltersOpen}
          showChevron={false}
          collapseOnMobile
          onClick={() => setFiltersOpen(true)}
        />
      </ToolbarRow>

      <Table
        columns={columns}
        rows={rows}
        size="lg"
        minTableWidth={getTableMinWidth(columns)}
        isLoading={isLoading}
        emptyState={
          <EmptyState
            title={
              activeFiltersCount || debouncedSearch
                ? NO_MATCHING_MEMBERS_MESSAGE
                : NO_MEMBERS_MESSAGE
            }
          />
        }
        renderExpandedRow={(row) =>
          row.member.fundAssociations.length ? (
            <Collapse in={row.isOpen} timeout={REVEAL_DURATION_MS} unmountOnExit>
              <ExpandedRowWrapper>
                <MemberFundsTable
                  funds={row.member.fundAssociations}
                  email={row.member.email}
                  isEntityMember={!!row.member.entityMemberId}
                  isRejectedUser={row.member.status === 'Rejected'}
                />
              </ExpandedRowWrapper>
            </Collapse>
          ) : null
        }
      />

      <LoadMoreSentinel ref={loadMoreRef} />
      {isFetchingNextPage && (
        <LoadMoreSpinner>
          <Spinner />
        </LoadMoreSpinner>
      )}

      <UserDetailsFiltersDrawer
        open={isFiltersOpen}
        value={{ role: filters.role, status: filters.status }}
        onClose={() => setFiltersOpen(false)}
        onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
      />
    </UserDetailsRoot>
  );
};
